import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useRouteParams } from '../_hooks/useRouteParams';
import { GainLossTab } from '../AccountPerformanceReporting/tabs/GainLoss/GainLoss';
import { Alert } from '../CommonComponents/OPL/Alert/Alert';
import { ErrorState } from '../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../CommonComponents/OPL/Box/Box';
import { Button } from '../CommonComponents/OPL/Button/Button';
import { Input } from '../CommonComponents/OPL/Input/Input';
import { Loading } from '../CommonComponents/OPL/Loading/Loading';
import { SegmentedControl } from '../CommonComponents/OPL/SegmentedControl/SegmentedControl';
import { Separator } from '../CommonComponents/OPL/Separator/Separator';
import { Tab } from '../CommonComponents/OPL/Tab/Tab';
import { TabList } from '../CommonComponents/OPL/Tab/TabList';
import { Tabs } from '../CommonComponents/OPL/Tab/Tabs';
import { toast } from '../CommonComponents/OPL/Toast/Toast';
import { PageWrapper } from '../CommonComponents/PageWrapper/PageWrapper';
import {
  DefaultWidgetsTemplateDTO,
  fetchDefaultTemplate,
  WidgetSlugDTO,
} from './api/useDefaultTemplateQuery';
import { useWidgetTemplateMutation } from './api/useWidgetTemplateMutation';
import { AddWidgetModal } from './components/AddWidgetModal/AddWidgetModal';
import { AddWidgets } from './components/AddWidgets/AddWidgets';
import { Header } from './components/Header/Header';
import { Widgets } from './components/Widgets/Widgets';

import { useTabLabels } from './hooks/useTabLabels';
import { FormValues, GAINLOSS_TAB_MAP, validation } from './PersonalFinanceTemplate.meta';
import { usePersonalFinanceTemplate } from './usePersonalFinanceTemplate';
import { queryKeys as settingsTemplateQueryKey } from '../AdviserSettings/Page/Templates/api/queryKeys';
import { queryKeys as personalFinanceTemplateQueryKey } from './api/queryKeys';

export const PersonalFinanceTemplate = () => {
  const { templateId } = useRouteParams<{ templateId: string }>();

  const { data, isLoading, error, refetch } = usePersonalFinanceTemplate();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Loading text="Loading" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const { widgets, name } = data;

  return (
    <PersonalFinanceTemplateContent templateId={templateId} data={widgets} templateName={name} />
  );
};

interface PersonalFinanceTemplateContentProps {
  templateId: string;
  data: DefaultWidgetsTemplateDTO;
  templateName: string;
}

const PersonalFinanceTemplateContent = ({
  templateId,
  data,
  templateName,
}: PersonalFinanceTemplateContentProps) => {
  const queryClient = useQueryClient();

  const { mutateAsync: saveTemplate, error } = useWidgetTemplateMutation();

  const formBag = useForm<FormValues>({
    resolver: yupResolver(validation),
    defaultValues: {
      id: templateId,
      name: templateName,
      widgets: data,
    },
  });

  const [activeTab, setActiveTab] = useState<WidgetSlugDTO>('Summary');

  const tabLabels = useTabLabels();

  const tabs = Object.entries(tabLabels).map(([tabKey, tabName]) => ({
    name: tabName,
    isActive: activeTab === tabKey,
    action: () => {
      setActiveTab(tabKey as WidgetSlugDTO);
    },
  }));

  const [isAddWidgetModalOpen, setIsAddWidgetModalOpen] = useState(false);
  const [currentTabIndex, setCurrentTabIndex] = useState<GainLossTab>(0);
  const gainLossActiveTab = GAINLOSS_TAB_MAP[currentTabIndex].slug;

  const onSubmit = formBag.handleSubmit(async values => {
    try {
      const { templateId: newTemplateId } = await saveTemplate({
        ...values,
        template: values.widgets,
      });

      toast.success('Template saved successfully');

      await queryClient.invalidateQueries(settingsTemplateQueryKey.personalFinanceTemplates());
      await queryClient.invalidateQueries(
        personalFinanceTemplateQueryKey.widgetTemplate(templateId),
      );

      if (!templateId && newTemplateId) {
        ADV.util.Router.go2('PersonalFinanceTemplate', {
          templateId: newTemplateId,
        });
      }
    } catch (err) {
      console.error(err);
    }
  });

  const onReset = async () => {
    const defaultWidgets = await fetchDefaultTemplate();

    formBag.reset({
      widgets: defaultWidgets || {},
    });

    toast.success('Template has been reset to default');
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Header isNew={!templateId} isDefault={templateId === 'default'} onReset={onReset} />

      {error && (
        <Box my="8">
          <Alert variant="error" message={error.message} />
        </Box>
      )}

      <Box
        as="form"
        id="personal-finance-template-form"
        display="flex"
        flex="1"
        flexDirection="column"
        css={({ theme }) => ({ gap: theme.space['16'] })}
        onSubmit={onSubmit}
      >
        <Box maxWidth="300px">
          <Input
            label="Template Name"
            {...formBag.register('name')}
            helperText={formBag.formState.errors.name?.message || 'Max. 40 characters'}
            formVariant={formBag.formState.errors.name ? 'error' : undefined}
            disabled={templateId === 'default'}
          />
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          css={({ theme }) => ({ gap: theme.space['24'] })}
        >
          <Tabs
            selectedIndex={tabs.findIndex(tab => tab.isActive)}
            onSelect={index => tabs[index].action()}
          >
            <TabList>
              {Object.entries(tabLabels).map(([key, name]) => (
                <Tab key={key}>{name}</Tab>
              ))}
            </TabList>
          </Tabs>

          <Alert
            message="This is sample data for preview purposes only. No real client data is included in templates."
            variant="info"
          />

          <FormProvider {...formBag}>
            <AddWidgets
              activeTab={activeTab === 'GainLoss' ? gainLossActiveTab : activeTab}
              onOpen={() => setIsAddWidgetModalOpen(true)}
            />

            {activeTab === 'GainLoss' ? (
              <PageWrapper>
                <SegmentedControl
                  id="default"
                  values={GAINLOSS_TAB_MAP.map(({ name }) => name)}
                  selectedIndex={currentTabIndex}
                  onChange={setCurrentTabIndex}
                  ariaLabel="Select gain loss type"
                  my="12"
                />

                <Widgets activeTab={gainLossActiveTab} key={currentTabIndex} />
              </PageWrapper>
            ) : (
              <Widgets activeTab={activeTab} key={activeTab} />
            )}
          </FormProvider>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" css={({ theme }) => ({ gap: theme.space['16'] })}>
        <Separator />

        <Box display="flex" justifyContent="space-between">
          <Button
            variant="secondary"
            size="large"
            leftIcon="fas fa-chevron-left"
            onClick={() => {
              ADV.util.Router.go2('AdviserSettings', {
                page: 'templates',
              });
            }}
          >
            Back
          </Button>

          {templateId !== 'default' && (
            <Button size="large" type="submit" form="personal-finance-template-form">
              Save Template
            </Button>
          )}
        </Box>
      </Box>

      {isAddWidgetModalOpen && (
        <AddWidgetModal
          activeTab={activeTab === 'GainLoss' ? gainLossActiveTab : activeTab}
          formBag={formBag}
          onClose={() => setIsAddWidgetModalOpen(false)}
        />
      )}
    </Box>
  );
};
